import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useBranchList } from "../api/branches";
import {
  ROLE_LABELS,
  UserRoleCode,
  UserRow,
  useUserList,
} from "../api/users";
import { useAuth } from "../auth/AuthContext";
import { PasswordResetModal } from "../components/users/PasswordResetModal";
import { ChangeOwnPasswordModal } from "../components/users/ChangeOwnPasswordModal";
import { UserFormModal } from "../components/users/UserFormModal";
import {
  ActivePassiveFilterSelect,
  DEFAULT_ACTIVE_PASSIVE_FILTER,
  type ActivePassiveFilter,
} from "../components/common/ActivePassiveFilterSelect";
import { formatPhoneDisplay } from "../utils/phone";
import { UI_LOADING } from "../constants/uiLabels";
import { ListPaginationBar, useListPageSize, usePagedOffset } from "../components/ListPagination";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./UsersPage.css";

function RoleBadge({ role }: { role: UserRoleCode | null }) {
  if (!role) return <span className="kp-muted">-</span>;
  return (
    <span className={`kp-user__badge kp-user__badge--${role}`}>{ROLE_LABELS[role]}</span>
  );
}

export function UsersPage() {
  const { session } = useAuth();
  const role = session?.user?.role as UserRoleCode | undefined;
  const callerId = session?.user?.id;
  const callerBranchId = session?.user?.branch_id;
  const isPlatformOwner = session?.user?.is_platform_owner === true;
  const isAdmin = role === "tenant_admin";
  const isManager = role === "manager";
  const isOfficeStaff = role === "office_staff";
  const isBranchStaff = isManager || isOfficeStaff;

  const branches = useBranchList({ isActive: true });
  // Tek lokasyon modelinde (musteri kurumu) sube secimi/gosterimi yok.
  // Cok subeli yonetim yalniz platform sahibi + birden fazla aktif sube varken.
  const activeBranchCount = branches.data?.length ?? 0;
  const showBranchUi = isPlatformOwner && activeBranchCount > 1;
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<{
    role?: UserRoleCode | "";
    branchId?: string;
    isActive?: "" | "true" | "false";
    q?: string;
  }>({ isActive: DEFAULT_ACTIVE_PASSIVE_FILTER });

  useEffect(() => {
    const branchId = searchParams.get("branch_id");
    if (branchId) {
      setFilter((prev) => (prev.branchId === branchId ? prev : { ...prev, branchId }));
    }
  }, [searchParams]);

  const pageSize = useListPageSize();
  const { offset, setOffset } = usePagedOffset(JSON.stringify([filter, pageSize]));
  const list = useUserList({ ...filter, limit: pageSize, offset });

  const [formModal, setFormModal] = useState<
    | { mode: "create" }
    | { mode: "edit"; user: UserRow }
    | null
  >(null);
  const [resetModal, setResetModal] = useState<UserRow | null>(null);
  const [changeOwnPasswordOpen, setChangeOwnPasswordOpen] = useState(false);
  const [pageError] = useState<string | null>(null);

  const rows = list.data?.rows || [];
  const total = list.data?.total ?? 0;

  useRegisterListPageStats({
    label: "kullanıcı",
    visible: rows.length,
    total,
    loading: list.loading,
  });

  const onFilterChange = (patch: Partial<typeof filter>) =>
    setFilter((prev) => ({ ...prev, ...patch }));

  const canManage = isAdmin || isManager || isOfficeStaff;

  return (
    <div className="kp-users">
      <div className="kp-users__header">
        <div>
          <h1>Kullanıcılar</h1>
          <p>
            Kullanıcıları yönetin. Toplam {total} kullanıcı.
            {isManager &&
              " Şube müdürü olarak kendi şubenizin büro personeli, eğitmen ve kursiyerlerini görebilirsiniz."}
            {isOfficeStaff &&
              " Büro personeli olarak kendi şubenizin eğitmen ve kursiyerlerini görebilirsiniz."}
          </p>
        </div>
        <div className="kp-users__header-actions">
          <button className="kp-btn" onClick={list.reload} disabled={list.loading}>
            Yenile
          </button>
          {canManage && (
            <button
              className="kp-btn kp-btn--primary"
              onClick={() => setFormModal({ mode: "create" })}
            >
              + Yeni Kullanıcı
            </button>
          )}
        </div>
      </div>

      <div className="kp-card kp-users__filters">
        <div className="kp-users__filters-row">
          <select
            className="kp-input"
            value={filter.role ?? ""}
            onChange={(e) =>
              onFilterChange({ role: (e.target.value as UserRoleCode) || undefined })
            }
          >
            <option value="">Tüm Roller</option>
            {isAdmin && <option value="tenant_admin">{ROLE_LABELS.tenant_admin}</option>}
            {isAdmin && <option value="manager">{ROLE_LABELS.manager}</option>}
            {(isAdmin || isManager) && (
              <option value="office_staff">{ROLE_LABELS.office_staff}</option>
            )}
            <option value="instructor">{ROLE_LABELS.instructor}</option>
            <option value="student">{ROLE_LABELS.student}</option>
          </select>

          {isAdmin && showBranchUi && (
            <select
              className="kp-input"
              value={filter.branchId ?? ""}
              onChange={(e) => onFilterChange({ branchId: e.target.value || undefined })}
            >
              <option value="">Tum subeler</option>
              {(branches.data || []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          )}

          <ActivePassiveFilterSelect
            value={(filter.isActive ?? DEFAULT_ACTIVE_PASSIVE_FILTER) as ActivePassiveFilter}
            onChange={(isActive) => onFilterChange({ isActive })}
          />

          <input
            type="text"
            className="kp-input"
            placeholder="Ad, e-posta veya telefon ara..."
            value={filter.q ?? ""}
            onChange={(e) => onFilterChange({ q: e.target.value || undefined })}
          />
        </div>
      </div>

      {pageError && <div className="kp-card kp-error">{pageError}</div>}
      {list.error && <div className="kp-card kp-error">{list.error}</div>}

      <div className="kp-card kp-users__table-wrap">
        {list.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {!list.loading && rows.length === 0 && (
          <div className="kp-empty">Bu Filtreyle Eşleşen Kullanıcı Bulunamadı.</div>
        )}
        {!list.loading && rows.length > 0 && (
          <table className="kp-users__table kp-data-table">
            <thead>
              <tr>
                <th>Ad Soyad</th>
                <th>İletişim</th>
                <th>Rol</th>
                {showBranchUi && <th>Şube</th>}
                <th>Durum</th>
                {canManage && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => {
                const isSelf = u.id === callerId;
                const inMyBranch = !isBranchStaff || u.branch_id === callerBranchId;
                const canEditRow =
                  canManage &&
                  inMyBranch &&
                  (isAdmin ||
                    (isManager &&
                      (u.role === "office_staff" ||
                        u.role === "instructor" ||
                        u.role === "student")) ||
                    (isOfficeStaff && (u.role === "instructor" || u.role === "student")));
                return (
                  <tr key={u.id} className={!u.is_active ? "kp-users__row--passive" : ""}>
                    <td>
                      <strong>{u.full_name}</strong>
                      {isSelf && <span className="kp-users__me">(siz)</span>}
                    </td>
                    <td>
                      <div className="kp-cell-nowrap">{u.email}</div>
                      {u.phone && (
                        <div className="kp-cell-phone kp-muted">{formatPhoneDisplay(u.phone)}</div>
                      )}
                    </td>
                    <td>
                      <RoleBadge role={u.role} />
                    </td>
                    {showBranchUi && (
                      <td>{u.branch_name || <span className="kp-muted">-</span>}</td>
                    )}
                    <td>
                      <span
                        className={`kp-users__status ${
                          u.is_active ? "kp-users__status--active" : "kp-users__status--passive"
                        }`}
                      >
                        {u.is_active ? "Aktif" : "Pasif"}
                      </span>
                    </td>
                    {canManage && (
                      <td>
                        <div className="kp-users__actions">
                          {canEditRow && (
                            <button
                              className="kp-btn kp-btn--ghost"
                              onClick={() => setFormModal({ mode: "edit", user: u })}
                            >
                              Düzenle
                            </button>
                          )}
                          {isSelf && (
                            <button
                              className="kp-btn kp-btn--ghost"
                              onClick={() => setChangeOwnPasswordOpen(true)}
                            >
                              Şifremi Değiştir
                            </button>
                          )}
                          {canEditRow && !isSelf && (
                            <button
                              className="kp-btn kp-btn--ghost"
                              onClick={() => setResetModal(u)}
                            >
                              Parola Sıfırla
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <ListPaginationBar
          total={total}
          offset={offset}
          limit={pageSize}
          loading={list.loading}
          onOffsetChange={setOffset}
        />
      </div>

      {formModal && (
        <UserFormModal
          mode={formModal.mode}
          initial={formModal.mode === "edit" ? formModal.user : undefined}
          canChooseRole={isAdmin}
          allowedCreateRoles={
            isManager
              ? ["office_staff", "instructor", "student"]
              : isOfficeStaff
                ? ["instructor", "student"]
                : undefined
          }
          canDeleteUser={
            isAdmin && formModal.mode === "edit" && formModal.user.id !== callerId
          }
          isSelf={formModal.mode === "edit" && formModal.user.id === callerId}
          canChooseBranch={isAdmin && showBranchUi}
          showBranchField={showBranchUi}
          defaultBranchId={
            isBranchStaff
              ? callerBranchId ?? null
              : activeBranchCount === 1
                ? branches.data?.[0]?.id ?? null
                : null
          }
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            list.reload();
          }}
        />
      )}

      {resetModal && (
        <PasswordResetModal
          user={resetModal}
          onClose={() => setResetModal(null)}
          onDone={() => list.reload()}
        />
      )}

      {changeOwnPasswordOpen && (
        <ChangeOwnPasswordModal onClose={() => setChangeOwnPasswordOpen(false)} />
      )}
    </div>
  );
}
